import { useEffect } from 'react';
import Navbar from '../../home/components/Navbar';
import Footer from '../../home/components/Footer';
import { SEO } from '../../../utils/seo';

export default function FireSafetyPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

  const schema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "serviceType": "Fire Safety Solutions",
    "provider": {
      "@type": "LocalBusiness",
      "name": "LEWIS SECURITY, RISK & COMPLIANCE SOLUTIONS",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "North Wales",
        "addressRegion": "Wales",
        "addressCountry": "GB"
      }
    },
    "areaServed": {
      "@type": "Place",
      "name": "North Wales"
    },
    "description": "Comprehensive fire alarm systems, emergency lighting, and fire risk assessments to ensure full compliance with UK fire safety regulations and protect lives.",
    "offers": {
      "@type": "Offer",
      "availability": "https://schema.org/InStock"
    },
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Fire Safety Services",
      "itemListElement": [
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Fire Alarm Systems",
            "description": "Advanced fire detection and alarm systems compliant with BS 5839 standards"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Emergency Lighting",
            "description": "Professional emergency lighting systems compliant with BS 5266"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Fire Risk Assessments",
            "description": "Comprehensive fire risk assessments compliant with Regulatory Reform (Fire Safety) Order 2005"
          }
        }
      ]
    }
  };

  return (
    <>
      <SEO
        title="Fire Safety North Wales - Fire Alarms Emergency Lighting - LEWIS SECURITY"
        description="Comprehensive fire alarm systems, emergency lighting, and fire risk assessments to ensure full compliance with UK fire safety regulations and protect lives. BAFE certified fire safety solutions in North Wales."
        keywords="fire safety North Wales, fire alarm systems, emergency lighting, fire risk assessment, BS 5839, BAFE certified, fire safety compliance"
        schema={schema}
      />
      <div className="min-h-screen bg-white">
        <Navbar />
        
        {/* Hero Section */}
        <section className="relative pt-32 pb-20 bg-gradient-to-br from-orange-900 via-red-900 to-gray-900">
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center text-white">
              <div className="w-20 h-20 bg-red-600 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <i className="ri-fire-line text-5xl text-white"></i>
              </div>
              <h1 className="text-5xl sm:text-6xl font-bold mb-6 font-poppins">Fire Safety Solutions</h1>
              <p className="text-xl text-gray-200 max-w-3xl mx-auto font-inter">
                Comprehensive fire alarm systems, emergency lighting, and fire risk assessments to ensure full compliance with UK fire safety regulations and protect lives
              </p>
            </div>
          </div>
        </section>

        {/* Main Content */}
        <section className="py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-16">
              <div>
                <h2 className="text-3xl font-bold text-gray-900 mb-6 font-poppins">Complete Fire Safety Protection</h2>
                <p className="text-gray-600 mb-6 leading-relaxed font-inter">
                  Fire safety is not just a legal requirement—it's essential for protecting lives and property. LEWIS SECURITY provides comprehensive fire safety solutions that meet and exceed all UK fire safety regulations, ensuring your premises are fully compliant and protected.
                </p>
                <p className="text-gray-600 mb-6 leading-relaxed font-inter">
                  Our BAFE-certified engineers design, install, and maintain fire alarm systems and emergency lighting tailored to your specific building requirements. We work with businesses across North Wales to create safer environments through professional fire safety management.
                </p>
              </div>
              <div className="relative h-96 rounded-2xl overflow-hidden shadow-2xl">
                <img 
                  src="https://public.readdy.ai/ai/img_res/b5779cddc6b3b4e9ecdd1480c929cc6f.jpg"
                  alt="Fire Safety Systems"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>

            {/* Services Grid */}
            <div className="mb-16">
              <h2 className="text-3xl font-bold text-gray-900 mb-10 text-center font-poppins">Our Fire Safety Services</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="bg-white rounded-xl p-8 shadow-lg border border-gray-100">
                  <div className="w-14 h-14 bg-red-50 rounded-lg flex items-center justify-center mb-4">
                    <i className="ri-alarm-warning-line text-3xl text-red-600"></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3 font-poppins">Fire Alarm Systems</h3>
                  <p className="text-gray-600 mb-4 font-inter">
                    Advanced fire detection and alarm systems designed to provide early warning of fire hazards. Our systems are fully compliant with BS 5839 standards and can be integrated with building management systems.
                  </p>
                  <ul className="space-y-2">
                    <li className="flex items-start text-sm text-gray-700 font-inter">
                      <i className="ri-check-line text-red-600 mr-2 text-lg mt-0.5"></i>
                      <span>Addressable and conventional systems</span>
                    </li>
                    <li className="flex items-start text-sm text-gray-700 font-inter">
                      <i className="ri-check-line text-red-600 mr-2 text-lg mt-0.5"></i>
                      <span>Smoke, heat, and multi-sensor detectors</span>
                    </li>
                    <li className="flex items-start text-sm text-gray-700 font-inter">
                      <i className="ri-check-line text-red-600 mr-2 text-lg mt-0.5"></i>
                      <span>Manual call points and sounders</span>
                    </li>
                    <li className="flex items-start text-sm text-gray-700 font-inter">
                      <i className="ri-check-line text-red-600 mr-2 text-lg mt-0.5"></i>
                      <span>Remote monitoring capabilities</span>
                    </li>
                  </ul>
                </div>

                <div className="bg-white rounded-xl p-8 shadow-lg border border-gray-100">
                  <div className="w-14 h-14 bg-red-50 rounded-lg flex items-center justify-center mb-4">
                    <i className="ri-lightbulb-flash-line text-3xl text-red-600"></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3 font-poppins">Emergency Lighting</h3>
                  <p className="text-gray-600 mb-4 font-inter">
                    Professional emergency lighting systems ensuring safe evacuation during power failures. Our installations comply with BS 5266 and provide reliable illumination when it matters most.
                  </p>
                  <ul className="space-y-2">
                    <li className="flex items-start text-sm text-gray-700 font-inter">
                      <i className="ri-check-line text-red-600 mr-2 text-lg mt-0.5"></i>
                      <span>LED emergency lighting fixtures</span>
                    </li>
                    <li className="flex items-start text-sm text-gray-700 font-inter">
                      <i className="ri-check-line text-red-600 mr-2 text-lg mt-0.5"></i>
                      <span>Exit signs and escape route lighting</span>
                    </li>
                    <li className="flex items-start text-sm text-gray-700 font-inter">
                      <i className="ri-check-line text-red-600 mr-2 text-lg mt-0.5"></i>
                      <span>Self-testing systems</span>
                    </li>
                    <li className="flex items-start text-sm text-gray-700 font-inter">
                      <i className="ri-check-line text-red-600 mr-2 text-lg mt-0.5"></i>
                      <span>Regular testing and certification</span>
                    </li>
                  </ul>
                </div>

                <div className="bg-white rounded-xl p-8 shadow-lg border border-gray-100">
                  <div className="w-14 h-14 bg-red-50 rounded-lg flex items-center justify-center mb-4">
                    <i className="ri-file-list-3-line text-3xl text-red-600"></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3 font-poppins">Fire Risk Assessments</h3>
                  <p className="text-gray-600 mb-4 font-inter">
                    Comprehensive fire risk assessments conducted by qualified assessors. We identify potential fire hazards, evaluate risks, and provide detailed action plans to ensure compliance with the Regulatory Reform (Fire Safety) Order 2005.
                  </p>
                  <ul className="space-y-2">
                    <li className="flex items-start text-sm text-gray-700 font-inter">
                      <i className="ri-check-line text-red-600 mr-2 text-lg mt-0.5"></i>
                      <span>Detailed hazard identification</span>
                    </li>
                    <li className="flex items-start text-sm text-gray-700 font-inter">
                      <i className="ri-check-line text-red-600 mr-2 text-lg mt-0.5"></i>
                      <span>Risk evaluation and prioritization</span>
                    </li>
                    <li className="flex items-start text-sm text-gray-700 font-inter">
                      <i className="ri-check-line text-red-600 mr-2 text-lg mt-0.5"></i>
                      <span>Actionable recommendations</span>
                    </li>
                    <li className="flex items-start text-sm text-gray-700 font-inter">
                      <i className="ri-check-line text-red-600 mr-2 text-lg mt-0.5"></i>
                      <span>Compliance documentation</span>
                    </li>
                  </ul>
                </div>

                <div className="bg-white rounded-xl p-8 shadow-lg border border-gray-100">
                  <div className="w-14 h-14 bg-red-50 rounded-lg flex items-center justify-center mb-4">
                    <i className="ri-tools-line text-3xl text-red-600"></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3 font-poppins">Maintenance & Servicing</h3>
                  <p className="text-gray-600 mb-4 font-inter">
                    Regular maintenance and servicing of fire safety systems to ensure they remain operational and compliant. Our preventative maintenance programs minimize downtime and extend system lifespan.
                  </p>
                  <ul className="space-y-2">
                    <li className="flex items-start text-sm text-gray-700 font-inter">
                      <i className="ri-check-line text-red-600 mr-2 text-lg mt-0.5"></i>
                      <span>Weekly, monthly, and annual testing</span>
                    </li>
                    <li className="flex items-start text-sm text-gray-700 font-inter">
                      <i className="ri-check-line text-red-600 mr-2 text-lg mt-0.5"></i>
                      <span>BAFE-certified engineers</span>
                    </li>
                    <li className="flex items-start text-sm text-gray-700 font-inter">
                      <i className="ri-check-line text-red-600 mr-2 text-lg mt-0.5"></i>
                      <span>Emergency repair services</span>
                    </li>
                    <li className="flex items-start text-sm text-gray-700 font-inter">
                      <i className="ri-check-line text-red-600 mr-2 text-lg mt-0.5"></i>
                      <span>Compliance certificates</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Compliance Section */}
            <div className="bg-gradient-to-br from-red-50 to-gray-50 rounded-2xl p-12 mb-16">
              <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center font-poppins">Fire Safety Compliance</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-4 font-poppins">UK Regulations We Cover</h3>
                  <ul className="space-y-3">
                    <li className="flex items-start text-gray-700 font-inter">
                      <i className="ri-checkbox-circle-fill text-red-600 mr-3 text-xl mt-0.5"></i>
                      <span><strong>Regulatory Reform (Fire Safety) Order 2005</strong> - Complete compliance for all commercial premises</span>
                    </li>
                    <li className="flex items-start text-gray-700 font-inter">
                      <i className="ri-checkbox-circle-fill text-red-600 mr-3 text-xl mt-0.5"></i>
                      <span><strong>BS 5839</strong> - Fire detection and alarm systems standards</span>
                    </li>
                    <li className="flex items-start text-gray-700 font-inter">
                      <i className="ri-checkbox-circle-fill text-red-600 mr-3 text-xl mt-0.5"></i>
                      <span><strong>BS 5266</strong> - Emergency lighting requirements</span>
                    </li>
                    <li className="flex items-start text-gray-700 font-inter">
                      <i className="ri-checkbox-circle-fill text-red-600 mr-3 text-xl mt-0.5"></i>
                      <span><strong>Building Regulations Approved Document B</strong> - Fire safety provisions</span>
                    </li>
                  </ul>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-4 font-poppins">Our Certifications</h3>
                  <ul className="space-y-3">
                    <li className="flex items-start text-gray-700 font-inter">
                      <i className="ri-award-fill text-red-600 mr-3 text-xl mt-0.5"></i>
                      <span><strong>BAFE SP203-1</strong> - Fire alarm design, installation, and maintenance</span>
                    </li>
                    <li className="flex items-start text-gray-700 font-inter">
                      <i className="ri-award-fill text-red-600 mr-3 text-xl mt-0.5"></i>
                      <span><strong>NSI Gold</strong> - Approved fire and security systems installer</span>
                    </li>
                    <li className="flex items-start text-gray-700 font-inter">
                      <i className="ri-award-fill text-red-600 mr-3 text-xl mt-0.5"></i>
                      <span><strong>FIA Registered</strong> - Fire Industry Association member</span>
                    </li>
                    <li className="flex items-start text-gray-700 font-inter">
                      <i className="ri-award-fill text-red-600 mr-3 text-xl mt-0.5"></i>
                      <span><strong>ISO 9001</strong> - Quality management systems</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* CTA Section */}
            <div className="text-center bg-gray-900 rounded-2xl p-12">
              <h2 className="text-3xl font-bold text-white mb-4 font-poppins">Ensure Your Fire Safety Compliance</h2>
              <p className="text-gray-300 mb-8 max-w-2xl mx-auto font-inter">
                Don't risk non-compliance. Contact us today for a comprehensive fire risk assessment and professional fire safety solutions
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a 
                  href="/#contact" 
                  className="inline-flex items-center justify-center px-8 py-4 bg-red-600 text-white font-semibold rounded-lg hover:bg-red-700 transition-colors duration-300 whitespace-nowrap cursor-pointer"
                >
                  Book Assessment
                  <i className="ri-arrow-right-line ml-2"></i>
                </a>
                <a 
                  href="tel:07834561015" 
                  className="inline-flex items-center justify-center px-8 py-4 bg-white text-gray-900 font-semibold rounded-lg hover:bg-gray-100 transition-colors duration-300 whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-phone-line mr-2"></i>
                  Call 07834 561015
                </a>
              </div>
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
}
